import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { catchError, from, Observable, switchMap, take, tap, throwError } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';
import { setAzureAccessToken } from '@app/state/actions/app.action';
import { AppState } from '@app/types/app.types';

function isTokenExpired(token: string): boolean {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000); // current time in seconds
    console.log('payload.exp < now ', payload.exp < now);
    console.log('payload.roles[0]', payload.roles[0]);
    return payload.exp < now;
    // return true; //to force return that the token has expired.
  } catch (e) {
    return true; // fail-safe: if it can't parse, assume it's expired
  }
}

function decodeJwt(token: string) {
  if (!token) {
    console.warn('decodeJwt: Token is null or empty. Cannot decode.');
    return null; // Return null if token is not valid
  }
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    console.error('Error decoding JWT token:', e);
    return null; // Return null on parsing error
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store<AppState>);
  const msalService = inject(MsalService);
  let refreshedToken;

  // Check if 'skipAuth' is set, and bypass token addition
  if (req.headers.has('skipAuth')) {
    const clonedReq = req.clone({
      headers: req.headers.delete('skipAuth'), // Remove 'skipAuth' before sending
    });
    return next(clonedReq);
  }

  const tokenSelector = req.headers.has('accessToken')
    ? (state: any) => state.app.azureAccessToken
    : (state: any) => state.app.azureIdToken;

  return store.select(tokenSelector).pipe(
    take(1),
    switchMap((currentTokenFromStore) => {
      let tokenUse = currentTokenFromStore;

      if (!tokenUse) {
        console.warn('Auth Interceptor: No token found in store. Proceeding without Authorization header.');
        return next(req); // Proceed with the original request
      }

      const account = msalService.instance.getActiveAccount() ?? undefined;
      const request: SilentRequest = {
        account,
        scopes: ['user.read'], // replace with actual scopes
      };

      return from(msalService.acquireTokenSilent(request)).pipe(
        switchMap((response) => {
          const newToken = req.headers.has('accessToken') ? response.accessToken : response.idToken;

          // Update store and session storage with the new tokens
          sessionStorage.setItem('accessToken', response.accessToken);
          sessionStorage.setItem('idToken', response.idToken);
          store.dispatch(setAzureAccessToken({ accessToken: response.accessToken, idToken: response.idToken }));
          const cloned = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
              'Content-Type': 'application/json',
            },
          });
          return next(cloned);
        }),
        catchError((err) => {
          console.log('error in intercepotor is ', err);
          if (err instanceof InteractionRequiredAuthError || err.errorCode === 'interaction_required') {
            console.warn('🔒 User interaction required. Redirecting to login...');
            msalService.loginRedirect(); // 👈 Force login redirect
            return throwError(() => new Error('Authentication required. Redirecting to login...'));
          }
          if (err.status === 403 && err.statusText === 'Forbidden') {
            // let this go to the API caller and then I can showcase error on UI
            console.warn('🚫 Access Forbidden (403). Propagating error to component.');
            // Re-throw the original HttpErrorResponse so the component can handle it.
            return throwError(() => err);
          }
          console.error('An unexpected error occurred in the interceptor:', err);
          // return throwError(() => err); // Re-throw the original errorreturn throwError(() => new Error('User interaction required. Redirecting to login...'));
          // If loginRedirect doesn't block navigation, let request fail
          return next(req);
        }),
      );
      // );
      // }
      // if (token) {
      //   console.log('HERE *********** token is there');
      //   req = req.clone({
      //     setHeaders: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      //   });
      //   return next(req);
      // }
      // return next(req);
    }),
  );
};
